import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  fetchTweet,
  updatePreviewSettings,
  loadHistory,
  resetTweet,
} from "../../app/features/tweets";
import { Loader2, AlertCircle, ArrowLeft } from "lucide-react";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import CustomizableTweetPreview from "../../components/preview/CustomizableTweetPreview";
import ThemePanel from "../../components/customization/ThemePanel";
import BackgroundPanel from "../../components/customization/BackgroundPanel";
import LayoutSettings from "../../components/customization/LayoutSettings";
import DisplaySettings from "../../components/customization/DisplaySettings";
import ExportButtons from "../../components/export/ExportButtons";
import Card from "../../components/ui/Card";
import { Footer } from "../../components";

const Generator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    tweetData,
    tweetUrl,
    isLoading,
    error,
    previewSettings,
    exportFormat,
  } = useSelector((state) => state.tweets);

  const [localUrl, setLocalUrl] = useState("");

  useEffect(() => {
    dispatch(loadHistory());
    document.documentElement.classList.add("dark");

    // Check if URL was passed from navigation
    if (location.state?.initialUrl) {
      setLocalUrl(location.state.initialUrl);
      dispatch(fetchTweet(location.state.initialUrl));
    }

    return () => {
      document.documentElement.classList.remove("dark");
    };
  }, [dispatch, location.state]);

  const handleFetch = () => {
    if (!localUrl.trim()) {
      return;
    }
    dispatch(fetchTweet(localUrl.trim()));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleFetch();
    }
  };

  const handleSettingsChange = (newSettings) => {
    dispatch(updatePreviewSettings(newSettings));
  };

  return (
    <div className="min-h-screen bg-black text-zinc-100 font-sans">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-black/60 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </button>
          <h1 className="text-lg font-bold text-white">Tweet Generator</h1>
          <div className="w-20"></div> {/* Spacer for centering */}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Left Column - Input & Customization */}
          <div className="lg:col-span-1 lg:h-[calc(100vh-8rem)] lg:flex lg:flex-col lg:overflow-hidden lg:pr-2">
            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden space-y-6 scroll-smooth lg:pr-2 pt-6">
              {/* URL Input */}
              <Card className="p-6">
                <h2 className="text-lg font-bold text-white mb-4">
                  Enter Tweet URL
                </h2>
                <div className="space-y-3">
                  <Input
                    placeholder="https://twitter.com/username/status/..."
                    value={localUrl}
                    onChange={(e) => setLocalUrl(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="w-full"
                    disabled={isLoading}
                  />
                  <Button
                    onClick={handleFetch}
                    disabled={isLoading || !localUrl.trim()}
                    className="w-full"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Fetching...
                      </>
                    ) : (
                      "Fetch Tweet"
                    )}
                  </Button>
                  {error && (
                    <div className="flex items-center gap-2 text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span>{error}</span>
                    </div>
                  )}
                  {tweetData && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        dispatch(resetTweet());
                        setLocalUrl("");
                      }}
                      className="w-full"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </Card>

              {/* Theme Customization */}
              <ThemePanel
                previewSettings={previewSettings}
                onSettingsChange={handleSettingsChange}
              />

              {/* Background Settings */}
              <BackgroundPanel
                previewSettings={previewSettings}
                onSettingsChange={handleSettingsChange}
              />

              {/* Layout Settings */}
              <LayoutSettings
                previewSettings={previewSettings}
                onSettingsChange={handleSettingsChange}
              />

              {/* Display Settings */}
              <DisplaySettings
                previewSettings={previewSettings}
                onSettingsChange={handleSettingsChange}
              />
            </div>

            {/* Sticky Export Buttons at Bottom */}
            <div className="flex-shrink-0 pt-6 border-t border-zinc-800 bg-black/60 backdrop-blur-sm lg:sticky lg:bottom-0">
              <Card className="p-6">
                <ExportButtons
                  tweetData={tweetData}
                  previewSettings={previewSettings}
                  exportFormat={exportFormat}
                />
              </Card>
            </div>
          </div>

          {/* Center Column - Preview */}
          <div className="lg:col-span-2 lg:h-[calc(100vh-8rem)] lg:overflow-y-auto lg:overflow-x-hidden lg:pr-2 scroll-smooth">
            <Card className="p-6 h-full flex flex-col">
              <div className="flex items-center justify-between mb-6 flex-shrink-0">
                <h2 className="text-lg font-bold text-white">Live Preview</h2>
                {tweetData && (
                  <div className="flex items-center gap-2 text-xs text-zinc-400">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                    <span>Ready to Export</span>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto overflow-x-hidden flex items-start justify-center min-h-0">
                {isLoading && !tweetData && (
                  <div className="flex flex-col items-center justify-center py-20 w-full">
                    <Loader2 className="w-12 h-12 animate-spin text-blue-500 mb-4" />
                    <p className="text-zinc-400">Fetching tweet data...</p>
                  </div>
                )}

                {!isLoading && !tweetData && !error && (
                  <div className="flex flex-col items-center justify-center py-20 text-center w-full">
                    <p className="text-zinc-400 mb-2">No tweet loaded</p>
                    <p className="text-sm text-zinc-500">
                      Enter a tweet URL and click "Fetch Tweet" to get started
                    </p>
                  </div>
                )}

                <div className="flex justify-center w-full py-4">
                  <CustomizableTweetPreview
                    tweetData={tweetData}
                    previewSettings={previewSettings}
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Generator;
